

import React, { useState, useEffect } from 'react';
import { Organization } from '../types';
import { LoadingSpinner } from './icons';

interface OrganizationModalProps {
    organization: Organization | null;
    onClose: () => void;
    onSave: (data: any, orgId: string | null) => Promise<void>;
}

const OrganizationModal: React.FC<OrganizationModalProps> = ({ organization, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        abn: '',
        contactPerson: '',
        email: '', // This is the organization's contact email, not the user login
        phone: '',
        userEmail: '',
        userPassword: '',
        confirmPassword: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isEditing = !!organization;

    useEffect(() => {
        if (isEditing && organization) {
            setFormData({
                name: organization.name,
                abn: organization.abn,
                contactPerson: organization.contactPerson,
                email: organization.email,
                phone: organization.phone,
                userEmail: '', 
                userPassword: '',
                confirmPassword: '',
            });
        } else {
             setFormData({ name: '', abn: '', contactPerson: '', email: '', phone: '', userEmail: '', userPassword: '', confirmPassword: '' });
        }
    }, [organization, isEditing]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        if (isEditing && formData.userPassword && formData.userPassword !== formData.confirmPassword) {
            setError("Passwords do not match.");
            setIsLoading(false);
            return;
        }

        try {
            const orgData = {
                name: formData.name,
                abn: formData.abn,
                contactPerson: formData.contactPerson,
                email: formData.email,
                phone: formData.phone,
            };

            let dataToSend: any = orgData;

            if (!isEditing) {
                if (!formData.userEmail) {
                    setError("An admin login email is required for new networks.");
                    setIsLoading(false);
                    return;
                }
                // No password is collected here - the new admin gets an email
                // with a link to set their own password.
                dataToSend = {
                    orgData,
                    userData: { email: formData.userEmail }
                };
            } else {
                if (formData.userPassword) {
                    dataToSend.password = formData.userPassword;
                }
            }

            await onSave(dataToSend, organization?.id || null);
            onClose();
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
                <form onSubmit={handleSubmit}>
                    <h3 className="text-xl leading-6 font-bold text-gray-900 mb-4">{isEditing ? 'Edit Network' : 'Add New Network'}</h3>
                    
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Network Name</label>
                            <input name="name" type="text" required onChange={handleChange} value={formData.name} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">ABN</label>
                            <input name="abn" type="text" required onChange={handleChange} value={formData.abn} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Contact Person</label>
                            <input name="contactPerson" type="text" required onChange={handleChange} value={formData.contactPerson} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Contact Email</label>
                            <input name="email" type="email" required onChange={handleChange} value={formData.email} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                            <input name="phone" type="tel" required onChange={handleChange} value={formData.phone} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
                        </div>
                         {isEditing && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Terms Agreement</label>
                                <p className="mt-1 text-sm text-gray-600 bg-gray-100 p-2 rounded-md border">
                                    {organization.termsAgreedOn
                                        ? `Agreed on ${new Date(organization.termsAgreedOn).toLocaleString()}`
                                        : 'Agreement not recorded.'}
                                </p>
                            </div>
                        )}
                        
                        <div className="border-t pt-4 mt-4 space-y-4">
                             {!isEditing ? (
                                <>
                                    <h4 className="font-semibold">Initial User Account</h4>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">User Login Email</label>
                                        <input name="userEmail" type="email" required onChange={handleChange} value={formData.userEmail} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
                                        <p className="mt-1 text-xs text-gray-500">No password is set here - this address will receive an email with a link to set one.</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h4 className="font-semibold">Reset Network Admin Password</h4>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">New Password (optional)</label>
                                        <input name="userPassword" type="password" onChange={handleChange} value={formData.userPassword} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                                        <input name="confirmPassword" type="password" onChange={handleChange} value={formData.confirmPassword} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                     {error && <p className="mt-3 text-sm text-red-600 p-2 bg-red-50 rounded">{error}</p>}
                    
                    <div className="items-center px-4 py-3 mt-4 -mx-5 -mb-5 border-t flex justify-end gap-3 bg-gray-50 rounded-b-md">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-auto shadow-sm hover:bg-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={isLoading} className="px-4 py-2 bg-primary text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-primary-light disabled:bg-gray-400 flex items-center">
                            {isLoading ? <LoadingSpinner className="w-5 h-5 mr-2" /> : null}
                            {isEditing ? 'Save Changes' : 'Create Network'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OrganizationModal;